import React from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import {
  fetchData,
  setSelected,
} from '../../../PlanSelectionNewWhatIfs/PlanSelectionNewWhatIfsActions';
import { PlanForm } from '../PlanForm/PlanForm';
import { FormValues, COPY_PLAN_FORM_ID } from '../PlanForm/PlanForm.meta';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { useCopyPlanMutation } from '../../api/useCopyPlanMutation';
import { useSelectedPlan } from '../../hooks/useSelectedPlan';
import { useRouteParams } from '../../hooks/useRouteParams';
import { queryKeys } from '../../api/queryKeys';

interface Props {
  closeModal: () => void;
}

export const CopyPlanModal: React.FC<Props> = ({ closeModal }) => {
  const selectedPlan = useSelectedPlan();

  const { clientId } = useRouteParams();

  const dispatch = useDispatch();

  const { mutateAsync: copyPlan, isLoading: isCopyingPlan } = useCopyPlanMutation();

  const queryClient = useQueryClient();

  if (!selectedPlan) {
    return null;
  }

  const {
    description: selectedPlanDescription,
    name: selectedPlanName,
    planId: selectedPlanId,
  } = selectedPlan;

  const handleCopyPlan = async ({ name, description }: FormValues) => {
    try {
      const newPlanId = await copyPlan({
        clientId,
        id: selectedPlanId,
        name,
        description,
      });

      //  TODO (LEGACY): Remove after the migration is complete
      dispatch(
        fetchData(clientId, () => {
          dispatch(setSelected(newPlanId, clientId));
        }),
      );

      queryClient.invalidateQueries(queryKeys.proposedPlans(clientId));

      toast.success('Plan is copied successfully.');
    } catch (err) {
      toast.error('An error occurred. Please try again later.');
    }

    closeModal();
  };

  return (
    <Modal
      isOpen
      onRequestClose={closeModal}
      aria={{ labelledby: 'plan-selection--copy-plan-modal-heading' }}
    >
      <ModalHeader id="plan-selection--copy-plan-modal-heading">Copy Plan</ModalHeader>

      <ModalCloseButton disabled={isCopyingPlan} onClick={closeModal} />

      <ModalBody>
        <PlanForm
          formId={COPY_PLAN_FORM_ID}
          onSubmit={handleCopyPlan}
          defaultName={selectedPlanName}
          defaultDescription={selectedPlanDescription}
        />
      </ModalBody>

      <ModalFooter>
        <Box display="flex" justifyContent="flex-end">
          <Button disabled={isCopyingPlan} variant="subtleInactive" onClick={closeModal}>
            Cancel
          </Button>

          <Button type="submit" isLoading={isCopyingPlan} ml="12" form={COPY_PLAN_FORM_ID}>
            Copy Plan
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
